import { queryOptions } from "@tanstack/react-query"
import * as real from "src/pages/study/compliance/compliance.service"
import * as mock from "src/pages/study/compliance/compliance.mock"
import { selectService } from "@myvp/shared/src/functions/mock"
import {
  GetComplianceMetricsInput,
  GetComplianceTrendMetricsInput,
  ComplianceMetricsDimension,
} from "src/gql/graphql"
import { I18nMessageId } from "src/i18n/i18n-message-types"
import { QueryOptionsData } from "@myvp/shared/src/types"

const service =
  process.env.REACT_APP_release === "true"
    ? real
    : selectService({ mock, real })

export type FilterSection = keyof GetComplianceMetricsInput["filters"]

export const COMPLIANCE_FILTER_SECTIONS = [
  "countryIds",
  "siteIds",
  "participantIds",
  "eventIds",
  "surveyIds",
] as const

export const COMPLIANCE_FILTER_SECTIONS_LABELS: Record<
  FilterSection,
  {
    label: I18nMessageId
    allLabel: I18nMessageId
  }
> = {
  countryIds: {
    label: "sponsorStudyNav.countries",
    allLabel: "studyHome.compliance.filters.countryIds.all",
  },
  siteIds: {
    label: "sponsorStudyNav.sites",
    allLabel: "studyHome.compliance.filters.siteIds.all",
  },
  participantIds: {
    label: "sponsorStudyNav.participants",
    allLabel: "studyHome.compliance.filters.participantIds.all",
  },
  eventIds: {
    label: "studyHome.compliance.filters.events",
    allLabel: "studyHome.compliance.filters.eventIds.all",
  },
  surveyIds: {
    label: "studyHome.compliance.filters.surveys",
    allLabel: "studyHome.compliance.filters.surveyIds.all",
  },
}

export const getComplianceMetricsOptions = (
  args: GetComplianceMetricsInput
) => {
  return queryOptions({
    queryKey: ["getComplianceMetrics", args],
    queryFn: () => service.getComplianceMetrics(args),
  })
}

export type ComplianceFilterOptions = QueryOptionsData<
  typeof getComplianceFilterOptionsOptions
>

export const getComplianceFilterOptionsOptions = (args: {
  globalSponsorStudyId: string
}) => {
  return queryOptions({
    queryKey: ["getComplianceFilterOptions", args],
    queryFn: async () => {
      const result = await service.getComplianceFilterOptions()
      return {
        ...result,
        filterOptions: {
          ...result.filterOptions,
          countryCount: result.filterOptions.countries.length,
          siteCount: result.filterOptions.sites.reduce(
            (acc, curr) => acc + curr.sites.length,
            0
          ),
          participantCount: result.filterOptions.participantsBySite.reduce(
            (acc, curr) => acc + curr.participants.length,
            0
          ),
          eventCount: result.filterOptions.events.length,
          surveyCount: result.filterOptions.surveysByType.reduce(
            (acc, curr) => acc + curr.surveys.length,
            0
          ),
        },
      }
    },
  })
}

export const getComplianceTrendMetricsOptions = (
  args: GetComplianceTrendMetricsInput
) => {
  return queryOptions({
    queryKey: ["getComplianceTrendMetrics", args],
    queryFn: () => service.getComplianceTrendMetrics(args),
  })
}

export const TOO_MANY_DIMENSION_ITEMS_ERROR_KEY = "TOO_MANY_DIMENSION_ITEMS"

export const COMPLIANCE_DIMENSION_KEYS = {
  study: "study",
  countries: "countries",
  sites: "sites",
  participants: "participants",
} as const

export type ComplianceDimensionKey =
  (typeof COMPLIANCE_DIMENSION_KEYS)[keyof typeof COMPLIANCE_DIMENSION_KEYS]

export const dimensionMap: Record<
  ComplianceDimensionKey,
  ComplianceMetricsDimension
> = {
  [COMPLIANCE_DIMENSION_KEYS.study]: ComplianceMetricsDimension.Study,
  [COMPLIANCE_DIMENSION_KEYS.countries]: ComplianceMetricsDimension.Country,
  [COMPLIANCE_DIMENSION_KEYS.sites]: ComplianceMetricsDimension.Site,
  [COMPLIANCE_DIMENSION_KEYS.participants]:
    ComplianceMetricsDimension.Participant,
}

export const dimensionTitleMap: Record<ComplianceDimensionKey, I18nMessageId> =
  {
    // Study
    [COMPLIANCE_DIMENSION_KEYS.study]: "sponsorStudyNav.study",
    // Countries
    [COMPLIANCE_DIMENSION_KEYS.countries]: "sponsorStudyNav.countries",
    // Sites
    [COMPLIANCE_DIMENSION_KEYS.sites]: "sponsorStudyNav.sites",
    // Participants
    [COMPLIANCE_DIMENSION_KEYS.participants]: "sponsorStudyNav.participants",
  }

export const dimensionTypeLabelMap: Record<
  ComplianceMetricsDimension,
  I18nMessageId
> = {
  // Study
  [ComplianceMetricsDimension.Study]: "sponsorStudyNav.study",
  // Country
  [ComplianceMetricsDimension.Country]: "sponsorSites.countryHeader",
  // Site
  [ComplianceMetricsDimension.Site]: "sponsorOverview.tableHeaderSite",
  // Participant
  [ComplianceMetricsDimension.Participant]:
    "sponsorOverview.tableHeaderParticipant",
}

export type ChartData = QueryOptionsData<
  typeof getComplianceMetricsOptions
>["summaryMetrics"][number]

export type TrendMetric = QueryOptionsData<
  typeof getComplianceTrendMetricsOptions
>["trendMetrics"][number]
